import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  FiHome, 
  FiCpu,
  FiActivity,
  FiMonitor,
  FiX,
  FiUsers,
  FiSettings
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  isMobileOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, isMobileOpen, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  
  const menuItems = [
    { path: '/', icon: <FiHome />, label: 'Dashboard' },
    { path: '/automation', icon: <FiCpu />, label: 'Properties' },
    { path: '/app-login-activity', icon: <FiActivity />, label: 'App Login Activity' },
    { path: '/remote-server', icon: <FiMonitor />, label: 'Remote Server' },
    { path: '/settings', icon: <FiSettings />, label: 'Settings' },
  ];

  // Add admin-only menu items
  if (isAdmin) {
    menuItems.push({ 
      path: '/users', 
      icon: <FiUsers />, 
      label: 'User Management' 
    });
  }

  // Debug function for Settings click
  const handleNavClick = (path: string) => {
    console.log('Navigation clicked:', path);
    if (window.innerWidth < 768) {
      onClose();
    }
    // If Settings path, use programmatic navigation as fallback
    if (path === '/settings') {
      console.log('Settings navigation triggered');
      navigate('/settings');
    }
  };

  return (
    <aside className={`sidebar ${isOpen ? 'sidebar-expanded' : 'sidebar-collapsed'} ${isMobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-header">
        <div className="logo">
          {isOpen && <span className="logo-text">CGR Automation</span>}
          {!isOpen && <span className="logo-icon">CG</span>}
        </div>
        <button className="close-btn mobile-only" onClick={onClose}>
          <FiX />
        </button>
      </div>

      <nav className="sidebar-nav">
        <ul className="nav-list">
          {menuItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'active' : ''}`
                }
                onClick={() => handleNavClick(item.path)}
                title={!isOpen ? item.label : undefined}
                style={{ cursor: 'pointer' }}
              >
                <span className="nav-icon">{item.icon}</span>
                {isOpen && <span className="nav-text">{item.label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="sidebar-footer">
        {isOpen && user && (
          <div className="user-info">
            <div className="user-avatar">{user.username[0].toUpperCase()}</div>
            <div className="user-details">
              <span className="user-name">{user.username}</span>
              <span className="user-role">{user.role === 'admin' ? 'Administrator' : 'User'}</span>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
